import * as React from "react"
import { TextareaBaseProps } from "@repo/design-system/types"
import { cn } from "@/lib/utils"

export interface WebTextareaProps extends TextareaBaseProps {
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';
}

export const textareaVariants = {
  base: "flex min-h-[80px] w-full premium-radius-md border border-input bg-transparent text-foreground px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
  variant: {
    default: "border-input",
    filled: "bg-muted border-transparent",
    outlined: "border-2",
    unstyled: "border-none shadow-none",
    ghost: "border-none bg-transparent shadow-none",
    error: "border-red-500 focus-visible:ring-red-500",
    success: "border-green-500 focus-visible:ring-green-500",
  },
};

const Textarea = React.forwardRef<HTMLTextAreaElement, WebTextareaProps>(
  ({ 
    className, 
    variant = "default",
    state,
    label,
    helperText,
    error,
    disabled,
    required,
    id,
    radius,
    ...props 
  }, ref) => {
    const variantClass = textareaVariants.variant[variant as keyof typeof textareaVariants.variant] || textareaVariants.variant.default;
    const radiusClass = radius ? `premium-radius-${radius}` : "premium-radius-input";

    return (
      <div className="relative w-full" suppressHydrationWarning>
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative" suppressHydrationWarning>
          <textarea
            className={cn(
              textareaVariants.base,
              variantClass,
              error && textareaVariants.variant.error,
              state === "error" && textareaVariants.variant.error,
              state === "success" && textareaVariants.variant.success,
              radiusClass,
              className
            )}
            ref={ref}
            disabled={disabled}
            required={required}
            id={id}
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
            data-no-autofill="true"
            data-lastpass-ignore="true"
            data-bitwarden-ignore="true"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            suppressHydrationWarning
            {...props}
          />
        </div>
        {(helperText || error) && (
          <p className={cn(
            "mt-1 text-sm",
            error ? "text-red-500" : "text-gray-500 dark:text-gray-400"
          )}>
            {error || helperText}
          </p>
        )}
      </div>
    )
  }
)
Textarea.displayName = "Textarea"

export { Textarea }
